import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useThemeStore } from '@/stores/theme';
import { useAuthStore } from '@/stores/auth';
import { useGamificationStore } from '@/stores/gamification';
import { Sidebar } from './Sidebar';
import { TopNav } from './TopNav';
import { AIChatSidebar } from '@/components/editor/AIChatSidebar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sparkles } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { isSupplierPortalUser } from '@/lib/supplier-portal-user';

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPath: string;
  onNavigate: (path: string) => void;
}

export function DashboardLayout({ children, currentPath, onNavigate }: DashboardLayoutProps) {
  const { sidebarCollapsed } = useThemeStore();
  const user = useAuthStore((s) => s.user);
  const fetchGamification = useGamificationStore((s) => s.fetch);
  const resetGamification = useGamificationStore((s) => s.reset);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => { setMobileMenuOpen(false); }, [currentPath]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = () => { if (desktop.matches) setMobileMenuOpen(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!user?.id) {
      resetGamification();
      return;
    }
    void fetchGamification(user.id);
  }, [user?.id, currentPath, fetchGamification, resetGamification]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-current-width',
      sidebarCollapsed ? '68px' : '260px'
    );
  }, [sidebarCollapsed]);

  const roles = (user as { roles?: string[] } | null)?.roles ?? [];
  const supplierPortal = isSupplierPortalUser(roles);

  const pageTitles: Record<string, string> = {
    '/dashboard': supplierPortal ? 'Zulieferer-Portal' : 'Heute',
    '/supplier-portal': 'Zulieferer-Portal',
    '/profile': 'Profil',
    '/marketing-plan': 'Marketingplan',
    '/marketing-strategy': 'Strategie',
    '/activities': 'Aktivitäten',
    '/calendar': 'Gesamtkalender',
    '/planner': 'Content-Planner',
    '/composer': 'Composer',
    '/social': 'Social Hub',
    '/social/accounts': 'Social Hub',
    '/social/queue': 'Social Hub',
    '/media': 'Medien',
    '/campaigns': 'Kampagnen',
    '/statistics': 'Insights',
    '/automation': 'Automation',
    '/suppliers': 'Zulieferer',
    '/teams': 'Teams',
    '/documents': 'Dokumente',
    '/notifications': 'Posteingang',
    '/onboarding': 'Onboarding',
    '/companion': 'Companion',
    '/ai': 'Companion',
    '/help': 'Hilfe',
    '/admin/users': 'Benutzer',
    '/admin/roles': 'Rollen',
    '/admin/integrations': 'Integrationen',
    '/admin/settings': 'Einstellungen',
    '/admin/notifications': 'Benachrichtigungen',
    '/admin/audit': 'Audit-Log',
  };

  return (
    <TooltipProvider delayDuration={250}>
    <div className="app-shell min-h-screen min-w-0 bg-background">
      <div className="hidden md:block"><Sidebar currentPath={currentPath} onNavigate={onNavigate} /></div>
      <DialogPrimitive.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
      <TopNav currentPath={currentPath} onNavigate={onNavigate} />
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-scrim md:hidden" />
          <DialogPrimitive.Content aria-describedby={undefined} className="fixed inset-y-0 left-0 z-50 w-[min(300px,calc(100vw-3rem))] bg-card shadow-lg md:hidden">
            <DialogPrimitive.Title className="sr-only">Hauptnavigation</DialogPrimitive.Title>
            <Sidebar mobile currentPath={currentPath} onNavigate={(path) => { setMobileMenuOpen(false); onNavigate(path); }} onClose={() => setMobileMenuOpen(false)} />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <main
        className={cn(
          'min-w-0 pt-16 transition-[padding] duration-200',
          sidebarCollapsed ? 'md:pl-[68px]' : 'md:pl-[260px]'
        )}
      >
        <div className="min-h-[calc(100vh-64px)] min-w-0 p-4 sm:p-6">
          {children}
        </div>
      </main>

      {!supplierPortal && (
        <button
          type="button"
          onClick={() => setAiChatOpen(true)}
          className={cn(
            'fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition-colors hover:bg-background dark:hover:bg-card-hover',
            aiChatOpen && 'hidden'
          )}
          title="KI-Assistent öffnen"
        >
          <Sparkles size={20} />
        </button>
      )}

      {!supplierPortal && (
        <AIChatSidebar
          isOpen={aiChatOpen}
          onClose={() => setAiChatOpen(false)}
          context={pageTitles[currentPath] ? { type: 'page', title: pageTitles[currentPath] } : undefined}
        />
      )}
    </div>
    </TooltipProvider>
  );
}
